/**
 * One offline deletion request awaiting an admin decision.
 *
 * Every offline deletion arrives here, not only the ones a replay refused:
 * deletion is the one offline change that cannot be corrected afterwards.
 *
 * The card shows the order as it looked when the deletion was requested, and,
 * when a replay found the cloud had moved, what it looks like now. That
 * difference is the whole reason to hesitate — someone worked on this order
 * during the outage.
 *
 * See TCA-26081406.
 */
import React from 'react';
import { Loader2, RotateCcw, Trash2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDeliveryDateDisplay } from '@/utils/dateUtils';

const SNAPSHOT_FIELDS = [
  ['order_number', 'External order #'],
  ['status', 'Status'],
  ['recipient_name', 'Recipient'],
  ['delivery_date', 'Delivery date'],
];

const displayValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (field === 'delivery_date') return formatDeliveryDateDisplay(value);
  return String(value);
};

const SnapshotRows = ({ snapshot }) => (
  <>
    {SNAPSHOT_FIELDS.map(([field, label]) => (
      <div key={field}>
        <span className="text-stone-500">{label}: </span>
        <span>{displayValue(field, snapshot[field])}</span>
      </div>
    ))}
  </>
);

const DeleteRequestCard = ({
  item,
  isAdmin,
  resolvingId,
  onApprove,
  onReject,
}) => {
  const mutation = item.mutation || {};
  const order = item.order || {};
  const base = mutation.base_snapshot || {};
  const cloud = mutation.cloud_order_snapshot || mutation.cloud_snapshot || null;
  const busy = resolvingId === mutation.id;
  // A replay already refused this one: the cloud record moved after the
  // deletion was requested, so approving now destroys that newer work too.
  const contested = mutation.state === 'conflict';

  return (
    <Card
      className={contested ? 'border-red-400 bg-red-50' : 'border-stone-300 bg-stone-50'}
      data-testid={`cached-delete-request-${mutation.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trash2 className={`h-4 w-4 ${contested ? 'text-red-700' : 'text-stone-600'}`} />
              Offline deletion request
              <span className="text-stone-900">
                {order.internal_order_number || mutation.target_id}
              </span>
            </CardTitle>
            <CardDescription>
              {order.recipient_name || base.recipient_name || 'Cached order'}
              {order.delivery_date ? ` · ${formatDeliveryDateDisplay(order.delivery_date)}` : ''}
              {mutation.actor_username ? ` · requested offline by ${mutation.actor_username}` : ''}
            </CardDescription>
          </div>
          {isAdmin ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => onReject(item)}
                data-testid={`keep-order-${mutation.id}`}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Keep order
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={busy}
                onClick={() => onApprove(item)}
                data-testid={`confirm-delete-order-${mutation.id}`}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Delete permanently
              </Button>
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {contested ? (
          <div className="flex items-start gap-2 rounded border border-red-300 bg-white p-3 text-red-800">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              This order changed after the deletion was requested. Deleting it now
              also destroys that newer work.
            </span>
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="font-medium text-stone-700">When deletion was requested</div>
            <SnapshotRows snapshot={base} />
          </div>
          {cloud ? (
            <div className="space-y-1">
              <div className="font-medium text-stone-700">In the cloud now</div>
              <SnapshotRows snapshot={cloud} />
            </div>
          ) : null}
        </div>
        <p className="text-stone-500">
          Nothing has been deleted yet. The order is hidden from the rest of the
          app until this is decided.
        </p>
      </CardContent>
    </Card>
  );
};

export default DeleteRequestCard;
